import { Link } from "@tanstack/react-router";
import { Building2, ChevronRight, Lock, Plus, Settings } from "lucide-react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useMe } from "@/lib/auth";

/**
 * ObraSelectorPage es el punto de entrada por obra (F7 #157). El usuario con más de una
 * obra elige aquí en cuál trabajar; con una sola obra el `beforeLoad` de la ruta lo envía
 * directo a ella —salvo que haya pedido el selector a propósito (`?elegir=true`, #265), en
 * cuyo caso esta lista sí se renderiza con su única obra— y con cero obras se muestra el
 * estado vacío. El admin global ve todas las obras que devuelve `/me`. La navegación
 * es por URL (`/obras/$obraId`); el selector visual de la cabecera es #160.
 */
export function ObraSelectorPage() {
  const { data: me } = useMe();

  if (!me) {
    return null;
  }

  // El admin global gestiona la colección de obras (crear/editar/baja + miembros y
  // autorizaciones) desde una pantalla dedicada; el botón solo se muestra a los admins.
  const gestionarObras = me.es_admin ? (
    <Button variant="outline" asChild>
      <Link to="/gestion/obras">
        <Settings className="h-5 w-5" aria-hidden="true" />
        Gestionar obras
      </Link>
    </Button>
  ) : null;

  if (me.obras.length === 0) {
    return (
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Obras</h1>
        <Alert variant="info">
          <Building2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {me.es_admin
              ? "Todavía no hay obras. Crea la primera para empezar a organizar el trabajo."
              : "No tienes obras asignadas. Contacta con el administrador."}
          </span>
        </Alert>
        {me.es_admin ? (
          // Atajo de primer arranque: abre directamente el alta en la gestión de obras (#187).
          <Button className="self-start" asChild>
            <Link to="/gestion/obras" search={{ nueva: true }}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Crear la primera obra
            </Link>
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Elige una obra</h1>
          <p className="text-base text-slate-500">Selecciona la obra en la que vas a trabajar.</p>
        </div>
        {gestionarObras}
      </div>
      <ul className="flex flex-col gap-3">
        {me.obras.map((obra) => (
          <li key={obra.id_obra}>
            <Link
              to="/obras/$obraId"
              params={{ obraId: String(obra.id_obra) }}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                <Lock className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-semibold text-slate-900">{obra.nombre}</span>
                <span className="text-slate-500 text-sm">
                  {obra.codigo_obra} · {obra.rol}
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
